// src/pages/TaxInvoice.tsx
import React from "react";
import "./styles/invoice.css"
import { InvoiceTemplateData } from "../types/invoiceTemplate";

interface TaxInvoiceProps {
  order: any;
  invoice: any;
  templateData: InvoiceTemplateData;
}

const TaxInvoice: React.FC<TaxInvoiceProps> = ({ order, invoice, templateData }) => {
  const { supplierInformation, signature, footer, bankDetails, paymentMode } = templateData;

  return (
    <div className="invoice-page">
      {/* Watermark */}
      <div
        className={`watermark ${
          invoice.payment_status === "paid" ? "watermark-paid" : "watermark-pending"
        }`}
      >
        {invoice.payment_status === "paid" ? "PAID" : "PAYMENT PENDING"}
      </div>

      {/* Header */}
      <header className="invoice-header">
        <div className="brand-block">
          <img src="/assets/images/logos/7HillsLabLogo.png" alt="Company Logo" className="company-logo" />
        </div>
        <div className="document-title">
          <h1>Tax Invoice</h1>
          <div>Original for Recipient</div>
        </div>
        <div className="qr-block">
          <img src="/assets/images/payment/rzp_qr_code.png" alt="QR Code" className="qr-code" />
          <div className="qr-caption">SCAN & PAY WITH ANY UPI APP</div>
        </div>
      </header>

      {/* Meta */}
      <section className="meta-box">
        <div>
          <strong>Invoice Version:</strong> {invoice.invoice_template_version ?? "1.0"}
        </div>
        <div className="meta-separator">|</div>
        <div>
          <strong>Invoice No:</strong> {invoice.invoice_number}
        </div>
        <div className="meta-separator">|</div>
        <div>
          <strong>Invoice Date:</strong>{" "}
          {new Date(invoice.invoice_date).toLocaleDateString()}
        </div>
        <div className="meta-separator">|</div>
        <div>
          <strong>Order No/Date:</strong> {new Date(order.order_date).toLocaleDateString()}
        </div>
      </section>

      {/* Supplier / Buyer */}
      <section className="two-column-box">
        <div className="info-column">
          <div className="section-title">Supplier Information</div>
          <div className="supplier-name">{supplierInformation.supplierName}</div>
          <div className="address">
            {supplierInformation.addressLine1} {supplierInformation.addressLine2}
            <br />
            {supplierInformation.addressLine3} {supplierInformation.addressLine4}
          </div>
          <div className="info-gap"></div>
          <div>PAN: {supplierInformation.pan}</div>
          <div>GSTN: {supplierInformation.gstn}</div>
        </div>

        <div className="info-column">
          <div className="section-title">Buyer Information</div>
          <div>Name: <strong>{order.requestor_name}</strong></div>
          <div>Contact #: {order.requestor_phone}</div>
          <div>Email: {order.requestor_email}</div>
        </div>
      </section>

      {/* Items */}
      <section className="items-section">
        <table className="line-items">
          <thead>
            <tr>
              <th className="line-number">Line.No</th>
              <th>Description</th>
              <th className="unit-column">Unit</th>
              <th className="amount-column">Program Fee</th>
              <th className="amount-column">Amount</th>
            </tr>
          </thead>
          <tbody>
            {order.items.map((item: any, idx: number) => (
              <tr key={item.id}>
                <td className="center">{idx + 1}</td>
                <td>
                  {item.description ?? "Program"}
                  {item.program_code && <div className="item-code">{item.program_code}</div>}
                </td>
                <td className="center">{item.unit}</td>
                <td className="right">{item.program_fee}</td>
                <td className="right">{item.total_amount}</td>
              </tr>
            ))}
            <tr className="grand-total-row">
              <td colSpan={4}>GRAND TOTAL (Incl. Taxes)</td>
              <td className="right">{invoice.commercial.final_amount}</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Amount in Words */}
      <section className="amount-words">
        <strong>Amount in Words:</strong>
        <span>{invoice.amount_in_words ?? ""}</span>
      </section>

      {/* Payment */}
      <section className="payment-box">
        <div>
          Payment Mode: <strong>{paymentMode}</strong>
        </div>
        <div>
          Currency: <strong>{invoice.commercial.currency}</strong>
        </div>
      </section>

      {/* Bank */}
      <section className="bank-box">
        <div className="section-title">Bank Details</div>
        <div>{bankDetails.accountName}</div>
        <div>{bankDetails.addressLine1}</div>
        <div>{bankDetails.addressLine2}</div>
        <div>{bankDetails.addressLine3}</div>
        <div>{bankDetails.addressLine4}</div>
        <div className="bank-account-row">
          <span><strong>Account No: {bankDetails.accountNumber}</strong></span>
          <span><strong>IFSC Code: {bankDetails.ifscCode}</strong></span>
        </div>
      </section>

      {/* Remarks */}
      <section className="remarks-box">
        <div className="section-title">Remarks</div>
        <div className="remarks-value">{}</div>
      </section>

      {/* Footer */}
      <footer className="document-footer">
        <div>{signature.generatedBy}</div>
        <div>Regd. Office: {footer.registeredOffice}</div>
        <div>{footer.line1}</div>
        <div>{footer.line2}</div>
        <div>{footer.line3}</div>
        <div>{footer.line4}</div>
      </footer>
    </div>
  );
};

export default TaxInvoice;
